/**
 * Local-calendar date helpers.
 *
 * Checkin dates (`applies_to`) are plain calendar days — FastAPI serializes the
 * `date` column as `YYYY-MM-DD` with no time and no zone. Every place that
 * builds or reads one of those strings has to stay in the user's local
 * calendar, because `Date` is an instant and the two standard conversions each
 * silently cross midnight:
 *
 *   `new Date(y, m, d).toISOString()` — local midnight rendered in UTC, which
 *   is the *previous* day at any positive offset.
 *
 *   `new Date('2026-08-27')` — a date-only string parses as UTC midnight, so
 *   reading it back with `getDate()` gives the *previous* day at any negative
 *   offset.
 *
 * Use these two instead. They are inverses, and both stay on the day the user
 * sees no matter where they are.
 */

/** Format a `Date` (or epoch ms) as local-calendar `YYYY-MM-DD`. */
export function toLocalDateStr(value: Date | number): string {
  const d = typeof value === 'number' ? new Date(value) : value;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Parse a `YYYY-MM-DD` string into its calendar parts, without going through
 * `Date`. Returns 1-based `month` so callers can format directly.
 */
export function parseLocalDateStr(dateStr: string): { year: number; month: number; day: number } {
  const [year, month, day] = dateStr.split('-').map(Number);
  return { year, month, day };
}

/** Format a `YYYY-MM-DD` string as a short `M/D` axis label. */
export function toShortLabel(dateStr: string): string {
  const { month, day } = parseLocalDateStr(dateStr);
  return `${month}/${day}`;
}

/**
 * Checkin time-of-day presentation.
 *
 * A checkin's time arrives as `applies_at` — "HH:MM:SS", or null/absent when
 * the checkin has none, which is most of them (the application could not write
 * a time at all until 2026-09; see `docs/checkin_time.md`). How it is *shown*
 * is a sticky per-user setting, `users.timestamp_display`:
 *
 *   none         nothing
 *   approximate  one of four words
 *   precise      a clock, "2:34pm"
 *
 * A checkin with no time renders nothing in every mode. That is the common
 * case, not the edge case, so every function here returns `null` for a missing
 * or unparseable `applies_at`, and callers render nothing on `null` — never an
 * em dash, a placeholder, or an empty span that would move the layout.
 *
 * Nothing persists a bucket. The words are a way of *rendering* a time (and,
 * in the composer, a shortcut for picking one), so these formatters are the
 * single place that decides how a stored time is shown.
 */

export type TimestampDisplay = 'none' | 'approximate' | 'precise';

export const TIMESTAMP_DISPLAY_MODES: readonly TimestampDisplay[] = [
  'none',
  'approximate',
  'precise',
] as const;

/**
 * How a checkin's time of day came to be, stored beside the time itself as
 * `applies_precision` (`checkins.applies_to_precision`).
 *
 * Distinct from `TimestampDisplay`, which happens to share two of its words:
 * that one is a per-user preference about *rendering*, this one is a fact
 * about a particular checkin's time. `approximate` there means "show me the
 * bucket word for whatever this time is"; here it means "this time came from a
 * bucket word in the first place".
 *
 * Recorded so later analysis can tell a time the user typed from one a drag
 * computed — which of them can be relied on for advice, and which is a guess.
 * It is also shown: the timeline never renders more detail than was recorded
 * (`formatCheckinTime`), and the time picker shows a picked word as the word.
 */
export type AppliesPrecision = 'none' | 'exact' | 'approximate' | 'inferred';

export type TimeBucket = 'morning' | 'afternoon' | 'evening' | 'night';

/**
 * The four buckets, as half-open ranges `[startMin, endMin)` in minutes since
 * midnight. `night` is the one that wraps midnight: 00:00–06:00 is the *start*
 * of the day, so it is a plain range below 06:00 rather than a continuation of
 * evening's. Every boundary belongs to the bucket it opens — 06:00 is morning,
 * 12:00 afternoon, 18:00 evening, 00:00 night.
 */
export const TIME_BUCKETS: ReadonlyArray<{ name: TimeBucket; startMin: number; endMin: number }> = [
  { name: 'night', startMin: 0, endMin: 6 * 60 },
  { name: 'morning', startMin: 6 * 60, endMin: 12 * 60 },
  { name: 'afternoon', startMin: 12 * 60, endMin: 18 * 60 },
  { name: 'evening', startMin: 18 * 60, endMin: 24 * 60 },
];

/**
 * Parse an `applies_at` wire value into minutes since midnight, or `null` when
 * there is no time. Seconds are accepted and ignored — every UI here is
 * minute-granular — and an out-of-range value is treated as no time rather
 * than clamped, since a bad value is a bug elsewhere and guessing would hide
 * it.
 */
function parseAppliesAt(appliesAt: string | null | undefined): number | null {
  if (typeof appliesAt !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(appliesAt.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** The bucket word for an `applies_at`, or `null` when it carries no time. */
export function timeBucket(appliesAt: string | null | undefined): TimeBucket | null {
  const mins = parseAppliesAt(appliesAt);
  if (mins === null) return null;
  const bucket = TIME_BUCKETS.find((b) => mins >= b.startMin && mins < b.endMin);
  return bucket ? bucket.name : null;
}

/**
 * The clock form of an `applies_at` — `2:34pm`, `9:05am`, `12:00pm`, `12:00am` —
 * or `null` when it carries no time. Lowercase meridiem with no space, no
 * leading zero on the hour, and both midnight and noon read as 12 rather than
 * 0, which is how a clock reads them.
 */
export function formatClock(appliesAt: string | null | undefined): string | null {
  const mins = parseAppliesAt(appliesAt);
  if (mins === null) return null;
  const hours24 = Math.floor(mins / 60);
  const minutes = mins % 60;
  const meridiem = hours24 < 12 ? 'am' : 'pm';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(minutes).padStart(2, '0')}${meridiem}`;
}

/**
 * What to render beside a checkin's date, given the user's mode and how the
 * time was set. `null` means render nothing — which covers both the `none`
 * mode and every timeless checkin under the other two.
 *
 * `precise` never shows more than was recorded: a time picked as "morning"
 * stays `morning` rather than claiming `9:00am`, and one the app worked out
 * (a drag, an accepted suggestion) reads `~9:30am`.
 */
export function formatCheckinTime(
  appliesAt: string | null | undefined,
  mode: TimestampDisplay | null | undefined,
  precision?: AppliesPrecision | null,
): string | null {
  if (mode === 'approximate') return timeBucket(appliesAt);
  if (mode === 'precise') {
    if (precision === 'approximate') return timeBucket(appliesAt);
    const clock = formatClock(appliesAt);
    return clock && precision === 'inferred' ? `~${clock}` : clock;
  }
  return null;
}

/**
 * Minutes since midnight as an `applies_at` that both the wire and an
 * `<input type="time">` accept — `"HH:MM"`, zero-padded. Out of range is
 * `null` for the same reason `parseAppliesAt` refuses to clamp.
 */
export function minutesToAppliesAt(mins: number | null | undefined): string | null {
  if (typeof mins !== 'number' || !Number.isFinite(mins)) return null;
  const whole = Math.trunc(mins);
  if (whole < 0 || whole > 24 * 60 - 1) return null;
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * The time a bucket word stands for when it is used to *pick* one: the middle
 * of its range — morning 09:00, afternoon 15:00, evening 21:00, night 03:00.
 *
 * Derived from `TIME_BUCKETS` rather than written out, so the word a checkin
 * can be given and the word it is rendered with cannot drift apart. `night` is
 * no exception: its range is the day's first six hours, so its midpoint falls
 * out of the same arithmetic as the other three.
 */
export function bucketMidpoint(bucket: TimeBucket): string | null {
  const range = TIME_BUCKETS.find((b) => b.name === bucket);
  if (!range) return null;
  return minutesToAppliesAt((range.startMin + range.endMin) / 2);
}

/**
 * An `applies_at` as an `<input type="time">` value. The server sends
 * `"HH:MM:SS"` and the input wants `"HH:MM"` (its default `step` is a minute);
 * no time is the empty string, which is also what the input emits when it is
 * cleared.
 */
export function toTimeInputValue(appliesAt: string | null | undefined): string {
  return minutesToAppliesAt(parseAppliesAt(appliesAt)) ?? '';
}

/**
 * An `<input type="time">` value as an `applies_at` for the wire. The empty
 * string — a cleared input — becomes `null`, never `"00:00"`. Midnight is a
 * time a user can deliberately set and it renders `12:00am`, so a cleared input
 * that sent it would stamp a time on a checkin that has none; that is the
 * distinction the storage flag exists for (`docs/checkin_time.md`).
 */
export function timeInputToAppliesAt(value: string | null | undefined): string | null {
  return minutesToAppliesAt(parseAppliesAt(value));
}

/* ---------------------------------------------------------------------------
 * Retiming a checkin by dragging it
 *
 * Dragging a checkin to a new position rewrites its time to the midpoint of
 * the two neighbours it now sits between. The rule is stated **in time, not in
 * screen position**: the timeline's sort direction is a parameter, not a
 * constant (`features.py` carries both `applies_to ASC, created_at ASC` and
 * its DESC twin), so "the one below" inverts between views and a rule phrased
 * that way would invert with it.
 * ------------------------------------------------------------------------- */

/** The last minute of a day, 23:59. The ±30 min edge rule never crosses
 *  midnight; changing day is the drop's job (`dropDay`). */
const LAST_MINUTE_OF_DAY = 24 * 60 - 1;

/**
 * How far from its only neighbour a checkin lands when it is dropped at an end
 * of the day and there is nothing on the far side to average against. Half an
 * hour is the same order as the gaps the gesture is used to close, and it is
 * clamped at both ends of the day rather than wrapping.
 */
export const REORDER_EDGE_STEP_MIN = 30;

/** One side of a drop. `null`/`undefined` means there is no checkin on that
 *  side at all, which is a different thing from one that has no time. */
type Neighbour = { applies_at?: string | null } | null | undefined;

/**
 * Minutes for a neighbour, for the purpose of bounding a drop.
 *
 * **An untimed neighbour counts as midnight, not as skipped over.** The column
 * behind `applies_at` is a `datetime` and a checkin with no time is stored at
 * 00:00, so `ORDER BY applies_to` genuinely sorts it at the head of its day,
 * interleaved with any timed checkins. Skipping it would take the bound from a
 * checkin further away than the one the user dropped next to, and the row
 * would then land on the wrong side of it — the gesture would lie about where
 * it put things. Counting it as midnight is reading back the position the
 * timeline already gave it.
 *
 * The cost is the degenerate case: dropped between two untimed checkins, a
 * checkin gets 00:00 and keeps the place `created_at` gives it, so the drag
 * appears not to stick. That is a true statement about a day whose checkins
 * have no times — no time would order a row against rows that have none — and
 * it is the same collision the midpoint rule already tolerates.
 */
function neighbourMinutes(n: Neighbour): number | null {
  if (!n) return null;
  return parseAppliesAt(n.applies_at) ?? 0;
}

/**
 * The time a checkin takes when it is dropped between `before` and `after` —
 * its chronologically earlier and later neighbours on the same day.
 *
 * - Both sides: the midpoint. 09:00 and 10:00 give 09:30.
 * - Only a later neighbour (dropped before the day's earliest): half an hour
 *   ahead of it, floored at 00:00 rather than wrapping into the day before.
 * - Only an earlier one (dropped after the day's latest): half an hour behind
 *   it, capped at 23:59.
 * - Neither — a day holding nothing but the dragged checkin: `null`, meaning
 *   there is nothing to retime, and nothing is written.
 *
 * A midpoint that lands on a time another checkin already has is **fine and is
 * not adjusted**: the within-day tiebreak is `created_at`, so two equal
 * timestamps still have a stable order, and nudging one aside would solve a
 * problem nobody has.
 */
export function reorderedAppliesAt(before: Neighbour, after: Neighbour): string | null {
  const b = neighbourMinutes(before);
  const a = neighbourMinutes(after);
  if (b === null && a === null) return null;
  if (b === null) return minutesToAppliesAt(Math.max(0, (a as number) - REORDER_EDGE_STEP_MIN));
  if (a === null) return minutesToAppliesAt(Math.min(LAST_MINUTE_OF_DAY, b + REORDER_EDGE_STEP_MIN));
  return minutesToAppliesAt(Math.round((b + a) / 2));
}

/**
 * The precision a drag-computed time is stored with.
 *
 * `inferred`, and never anything else: a reorder midpoint is arithmetic on two
 * neighbours, not a claim the user made about when something happened. It
 * lives here rather than at the call site so the fact sits with the function
 * that computes the time, and so nothing can reach for `'exact'` by habit.
 */
export const REORDER_PRECISION: AppliesPrecision = 'inferred';

/**
 * The precision an accepted LLM suggestion is stored with.
 *
 * `inferred`, the same value a drag midpoint takes, and for the same reason:
 * a model proposed the time and the user pressed a button that took all of
 * them at once, which is not the same act as naming a moment. The line the
 * column records runs between "the user said this" and "something worked it
 * out", and both of these fall on the second side of it
 * (`docs/checkin_time.md`).
 *
 * Editing a filled picker afterwards moves that one row across the line: the
 * picker reports `exact` or `approximate` for what the user just did, and the
 * row's claim about itself follows whatever last set it.
 */
export const SUGGESTED_PRECISION: AppliesPrecision = 'inferred';

/**
 * The time a dragged checkin takes, given one day's checkins **in the order
 * they appear on screen after the move** and which way that order runs.
 *
 * `ascending` is what turns screen position into time: under `most_recent` the
 * list reads newest-first, so the row above a checkin is its *later*
 * neighbour, not its earlier one. Reversing here is the only place the
 * direction is consulted, which is what lets the rest of the rule be written
 * in time alone.
 *
 * Returns `null` when nothing should be written — the checkin is not in the
 * list, or it is the day's only one.
 */
export function retimeForReorder(
  displayedDay: ReadonlyArray<{ id: number; applies_at?: string | null }>,
  movedId: number,
  ascending: boolean,
): string | null {
  const chrono = ascending ? displayedDay : [...displayedDay].reverse();
  const i = chrono.findIndex((c) => c.id === movedId);
  if (i < 0) return null;
  return reorderedAppliesAt(chrono[i - 1], chrono[i + 1]);
}

/**
 * Whether a drop lands between two checkins that both have **no time** — the
 * one arrangement in which the midpoint rule has nothing to work with.
 *
 * Both untimed neighbours count as midnight (see `neighbourMinutes`), so their
 * midpoint is 00:00, which is where they already are: the dragged checkin gets
 * a time that orders it against nothing, keeps the place `created_at` gives
 * it, and the gesture appears not to stick. That is the case the
 * time-assignment dialog exists for.
 *
 * It is deliberately *not* "the day has no times". With one timed neighbour
 * the midpoint rule already gives a sensible answer — dropped between an
 * untimed row and a 10:00 one, a checkin gets 05:00, which is where it was
 * dropped — and that path must keep running untouched. Nor is it an edge drop:
 * with only one neighbour the ±30 min rule moves the checkin clear of it, even
 * when that neighbour is untimed.
 *
 * Arguments are `retimeForReorder`'s: the day **as it appears on screen after
 * the move**, and which way that order runs.
 */
export function dropNeedsTimeAssignment(
  displayedDay: ReadonlyArray<{ id: number; applies_at?: string | null }>,
  movedId: number,
  ascending: boolean,
): boolean {
  const chrono = ascending ? displayedDay : [...displayedDay].reverse();
  const i = chrono.findIndex((c) => c.id === movedId);
  if (i < 0) return false;
  const before = chrono[i - 1];
  const after = chrono[i + 1];
  if (!before || !after) return false;
  return parseAppliesAt(before.applies_at) === null && parseAppliesAt(after.applies_at) === null;
}

/**
 * Order one day's checkins the way the server does — by time, then by
 * `created_at`, with an untimed checkin at midnight — so an optimistic retime
 * can leave the row where the drop put it instead of waiting for a refetch.
 * `ascending` mirrors `retimeForReorder`'s.
 */
export function sortDayByTime<T extends { applies_at?: string | null; created_at?: string }>(
  rows: ReadonlyArray<T>,
  ascending: boolean,
): T[] {
  const dir = ascending ? 1 : -1;
  return [...rows].sort((x, y) => {
    const dt = (parseAppliesAt(x.applies_at) ?? 0) - (parseAppliesAt(y.applies_at) ?? 0);
    if (dt !== 0) return dir * dt;
    return dir * String(x.created_at ?? '').localeCompare(String(y.created_at ?? ''));
  });
}

/* ---------------------------------------------------------------------------
 * Which day a drop lands on
 *
 * While reordering, the timeline carries a divider at every change of day, and
 * each divider is a slot of its own in the list. So the gap between one day's
 * last row and the next day's first is two places, above the line and below
 * it, and a drop always says which day it means: the checkin joins the day of
 * the **section** it lands in — the rows between the nearest dividers on either
 * side of it, or an end of the list (`docs/checkin_time.md`).
 * ------------------------------------------------------------------------- */

/** The divider between two consecutive days on the timeline, named in time.
 *  `earlier` and `later` are the days on its two sides, whichever way the
 *  list runs on screen. */
export type DayBoundary = { boundary: { earlier: string; later: string } }

/** A checkin row as the drop rule needs it. */
type DatedRow = { id: number; applies_to: string }

/** One slot of the reorderable timeline: a checkin, or a divider. */
export type TimelineSlot<R extends DatedRow = DatedRow> = R | DayBoundary

export const isDayBoundary = (slot: TimelineSlot<DatedRow>): slot is DayBoundary =>
  'boundary' in slot

const dayOfRow = (row: { applies_to: string }): string => String(row.applies_to).slice(0, 10)

/**
 * The divider between two days that sit next to each other on screen, in
 * either order. `YYYY-MM-DD` compares chronologically as a string, so which
 * one is earlier needs no sort direction.
 */
export function dayBoundary(a: string, b: string): DayBoundary {
  return a < b ? { boundary: { earlier: a, later: b } } : { boundary: { earlier: b, later: a } }
}

/**
 * The day a dragged checkin lands on, given the **whole displayed timeline,
 * dividers included,** in the order the drop produced, and which way it runs.
 *
 * The checkin joins the day of the section it sits in. A section holding
 * other rows has one day, since dividers stand at every change of day. A
 * section holding nothing but the moved checkin — it was its day's only row
 * and went back between that day's two dividers — stands for the day those
 * dividers face it with, which is read off the dividers rather than off a
 * neighbouring row, since every neighbouring row is on another day.
 *
 * Within the day, the time is `retimeForReorder`'s and
 * `dropNeedsTimeAssignment`'s call, on that day's rows alone.
 *
 * `null` when the checkin isn't there, or has nothing to be placed against.
 */
export function dropDay(
  displayed: ReadonlyArray<TimelineSlot>,
  movedId: number,
  ascending: boolean,
): string | null {
  const chrono = ascending ? displayed : [...displayed].reverse()
  const i = chrono.findIndex((s) => !isDayBoundary(s) && s.id === movedId)
  if (i < 0) return null

  let start = i
  while (start > 0 && !isDayBoundary(chrono[start - 1])) start--
  let end = i
  while (end < chrono.length - 1 && !isDayBoundary(chrono[end + 1])) end++

  const companion = chrono
    .slice(start, end + 1)
    .find((s): s is DatedRow => !isDayBoundary(s) && s.id !== movedId)
  if (companion) return dayOfRow(companion)

  const earlierLine = chrono[start - 1] as DayBoundary | undefined
  const laterLine = chrono[end + 1] as DayBoundary | undefined
  return earlierLine?.boundary.later ?? laterLine?.boundary.earlier ?? null
}
